import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD700', // Gold color
      light: '#FFED4E',
      dark: '#B8860B',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FFA500', // Orange-gold for secondary elements
      light: '#FFB84D',
      dark: '#CC8400',
      contrastText: '#000000',
    },
    background: {
      default: '#0A0A0A', // Very dark black
      paper: '#1A1A1A', // Slightly lighter black for cards/papers
    },
    surface: {
      main: '#2A2A2A', // For elevated surfaces
    },
    text: {
      primary: '#FFFFFF', // White text
      secondary: '#B8B8B8', // Light gray for secondary text
    },
    divider: '#333333',
    action: {
      hover: 'rgba(255, 215, 0, 0.08)', // Gold hover with transparency
      selected: 'rgba(255, 215, 0, 0.12)',
      disabled: '#666666',
      disabledBackground: '#333333',
    },
    error: {
      main: '#FF6B6B',
      light: '#FF8E8E',
      dark: '#CC5555',
    },
    warning: {
      main: '#FFB347',
      light: '#FFC570',
      dark: '#CC8F39',
    },
    info: {
      main: '#4ECDC4',
      light: '#71D7D0',
      dark: '#3EA39D',
    },
    success: {
      main: '#51CF66',
      light: '#74D885',
      dark: '#41A652',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#FFD700',
    },
    h2: {
      fontWeight: 700,
      color: '#FFD700',
    },
    h3: {
      fontWeight: 600,
      color: '#FFD700',
    },
    h4: {
      fontWeight: 600,
      color: '#FFD700',
    },
    h5: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h6: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    body1: {
      color: '#FFFFFF',
    },
    body2: {
      color: '#B8B8B8',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          '&.MuiButton-contained': {
            background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
            color: '#000000',
            '&:hover': {
              background: 'linear-gradient(45deg, #FFA500 30%, #FFD700 90%)',
              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
            },
          },
          '&.MuiButton-outlined': {
            borderColor: '#FFD700',
            color: '#FFD700',
            '&:hover': {
              borderColor: '#FFA500',
              backgroundColor: 'rgba(255, 215, 0, 0.08)',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#1A1A1A',
          border: '1px solid #333333',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(255, 215, 0, 0.15)',
            borderColor: '#FFD700',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0A0A0A',
          borderBottom: '1px solid #333333',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1A1A1A',
          border: '1px solid #333333',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0A0A0A',
          borderRight: '1px solid #333333',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#FFD700',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#FFA500',
            },
            '& .MuiListItemIcon-root': {
              color: '#000000',
            },
            '& .MuiListItemText-primary': {
              color: '#000000',
              fontWeight: 600,
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 215, 0, 0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#333333',
            },
            '&:hover fieldset': {
              borderColor: '#FFD700',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFD700',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#B8B8B8',
            '&.Mui-focused': {
              color: '#FFD700',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 215, 0, 0.08)',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#0A0A0A',
          borderTop: '1px solid #333333',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: '#B8B8B8',
          '&.Mui-selected': {
            color: '#FFD700',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A',
          border: '1px solid #333333',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#FFD700',
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;